import { ReactNode } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Chip, { ChipProps } from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import BloodtypeOutlined from '@mui/icons-material/BloodtypeOutlined';
import RemoveCircleOutlined from '@mui/icons-material/RemoveCircleOutlined';
import CheckCircleOutlined from '@mui/icons-material/CheckCircleOutlined';
import Inventory2Outlined from '@mui/icons-material/Inventory2Outlined';
import EventAvailableOutlined from '@mui/icons-material/EventAvailableOutlined';
import { Formatter } from '@omnigenbiodata/ui';
import DetailRow from '../DetailRow';
import { OrderStatus } from '../../../../../../../../core/types/common.types';

interface ParticipantSampleProps {
  orderStatus: OrderStatus;
  orderUpdatedTs: string | null;
  sampleBarcode: string | null;
  orderNum: string | null;
  orderSequencer: string | null;
  sampleViable: boolean | null;
  sampleLinkTs: string | null;
  sampleReceivedTs: string | null;
}

interface SampleSectionProps {
  title: string;
  icon: ReactNode;
  children: ReactNode;
}

const completedStatuses = [OrderStatus.RECEIVED, OrderStatus.STORED, OrderStatus.PROCESSED, OrderStatus.QCSUCCESS];
const warningStatuses = [OrderStatus.HOLD, OrderStatus.PENDING];
const errorStatuses = [OrderStatus.QCFAIL, OrderStatus.DESTROYED];

const formatDateTime = (value: string | null) =>
  value ? Formatter({ input: value, format: 'friendlyDateTime' }) : 'Not recorded';

function getStatusColour(orderStatus: OrderStatus): ChipProps['color'] {
  if (completedStatuses.includes(orderStatus)) {
    return 'success';
  }

  if (warningStatuses.includes(orderStatus)) {
    return 'warning';
  }

  if (errorStatuses.includes(orderStatus)) {
    return 'error';
  }

  return 'primary';
}

function getCardTone(orderStatus: OrderStatus) {
  if (completedStatuses.includes(orderStatus)) {
    return {
      bgcolor: 'success.lighter',
      borderColor: 'success.light',
      iconColor: 'success' as const,
    };
  }

  if (warningStatuses.includes(orderStatus)) {
    return {
      bgcolor: 'warning.lighter',
      borderColor: 'warning.light',
      iconColor: 'warning' as const,
    };
  }

  if (errorStatuses.includes(orderStatus)) {
    return {
      bgcolor: 'error.lighter',
      borderColor: 'error.light',
      iconColor: 'error' as const,
    };
  }

  return {
    bgcolor: 'background.paper',
    borderColor: 'divider',
    iconColor: 'primary' as const,
  };
}

function renderOrderStatus(orderStatus: OrderStatus) {
  const displayOrderStatus = orderStatus;

  return (
    <Chip
      label={displayOrderStatus}
      color={getStatusColour(displayOrderStatus)}
      size="small"
      sx={{ fontWeight: 700 }}
    />
  );
}

function renderSampleViability(sampleViable: boolean | null) {
  if (sampleViable === null) {
    return 'Not recorded';
  }

  return (
    <Chip
      icon={sampleViable ? <CheckCircleOutlined /> : <RemoveCircleOutlined />}
      label={sampleViable ? 'Viable' : 'Not viable'}
      color={sampleViable ? 'success' : 'error'}
      size="small"
      variant="outlined"
      sx={{
        fontWeight: 700,
        '& .MuiChip-icon': {
          fontSize: 18,
        },
      }}
    />
  );
}

function SampleSection({ title, icon, children }: SampleSectionProps) {
  return (
    <Box mt={2}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          mb: 1,
        }}
      >
        {icon}

        <Typography
          variant="subtitle1"
          component="h3"
          sx={{
            fontWeight: 700,
          }}
        >
          {title}
        </Typography>
      </Box>

      {children}
    </Box>
  );
}

function ParticipantSample({
  orderStatus,
  orderUpdatedTs,
  sampleBarcode,
  orderNum,
  orderSequencer,
  sampleViable,
  sampleLinkTs,
  sampleReceivedTs,
}: ParticipantSampleProps) {
  const displayOrderStatus = orderStatus;
  const tone = getCardTone(displayOrderStatus);

  return (
    <Box mb={2}>
      <Paper
        elevation={4}
        sx={{
          bgcolor: tone.bgcolor,
          border: 1,
          borderColor: tone.borderColor,
          overflow: 'hidden',
        }}
      >
        <Box p={2}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              minWidth: 0,
              gap: 1,
              mb: 1,
            }}
          >
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: 'background.paper',
                flexShrink: 0,
              }}
            >
              <BloodtypeOutlined color={tone.iconColor} />
            </Box>

            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h6" component="h2" sx={{ fontWeight: 700, m: 0 }}>
                Latest Sample Status
              </Typography>

              <Typography variant="body2" component="p" color="text.secondary">
                Current sample, order and processing details
              </Typography>
            </Box>
          </Box>

          <DetailRow label="Order Status" value={renderOrderStatus(displayOrderStatus)} />
          <DetailRow label="Sample Viable" value={renderSampleViability(sampleViable)} />

          <Divider sx={{ my: 2 }} />

          <SampleSection
            title="Sample Identifiers"
            icon={<Inventory2Outlined color={tone.iconColor} fontSize="small" />}
          >
            <DetailRow label="Sample Barcode" value={sampleBarcode} />
            <DetailRow label="Order No" value={orderNum} />
            <DetailRow label="Sequencer" value={orderSequencer} />
          </SampleSection>

          <Divider sx={{ my: 2 }} />

          <SampleSection title="Sample Dates" icon={<EventAvailableOutlined color={tone.iconColor} fontSize="small" />}>
            <DetailRow label="Last Updated" value={formatDateTime(orderUpdatedTs)} />
            <DetailRow label="Sample Linked" value={formatDateTime(sampleLinkTs)} />
            <DetailRow label="Sample Received" value={formatDateTime(sampleReceivedTs)} />
          </SampleSection>
        </Box>
      </Paper>
    </Box>
  );
}

export default ParticipantSample;
